// Reforma UX §6.2 (SESS-08) — modal "Nova conversa": Companion · Provedor · Modelo.
// Fallback: se a conta LLM não resolver (llmStatus desconectado/falha), pré-seleciona
// o 1º modelo disponível do provedor escolhido.
//
// Correção UX v2 §5.2.7 — a seleção de Provedor/Modelo é devolvida ao caller e
// PROPAGADA: a SessionsPage passa { provider_key, model_key } no 5º arg de
// api.sendMessage da PRIMEIRA mensagem (sessão criada pelo backend já com o
// override persistido em metadata.model_override).
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { ProviderSelect } from '@/components/llm/ProviderSelect';
import { ModelSelect } from '@/components/llm/ModelSelect';
import { api, type CompanionListItem } from '@/lib/api';

export interface NewConversationSelection {
  companionId: string;
  providerKey: string;
  modelKey: string;
}

interface NewConversationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selection: NewConversationSelection) => void | Promise<void>;
  companions: CompanionListItem[];
  companionsLoading?: boolean;
}

export function NewConversationModal({
  open,
  onClose,
  onConfirm,
  companions,
  companionsLoading,
}: NewConversationModalProps) {
  const [companionId, setCompanionId] = useState('');
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const activeCompanions = useMemo(
    () => companions.filter((c) => c.is_active),
    [companions],
  );

  // Conta LLM conectada → pré-seleciona provider/modelo dela.
  const { data: llmStatus } = useQuery({
    queryKey: ['llm-status'],
    queryFn: () => api.llmStatus(),
    staleTime: 60 * 1000,
    enabled: open,
    retry: false,
  });

  // Modelos do provider selecionado — mesma queryKey do ModelSelect (dedupe do
  // React Query) — usados pro fallback "1º modelo" quando a conta não resolve.
  const { data: modelsData } = useQuery({
    queryKey: ['llm-models', provider, 'text'],
    queryFn: () => api.listLlmModels({ provider, modality: 'text', page_size: 100 }),
    staleTime: 5 * 60 * 1000,
    enabled: open && !!provider,
  });

  // Defaults na abertura: 1 companion ativo → auto-seleciona; conta conectada →
  // provider/modelo da conta.
  useEffect(() => {
    if (!open) return;
    if (!companionId && activeCompanions.length === 1) {
      setCompanionId(activeCompanions[0].id);
    }
  }, [open, companionId, activeCompanions]);

  useEffect(() => {
    if (!open || provider) return;
    if (llmStatus?.connected && llmStatus.provider) {
      setProvider(llmStatus.provider);
      if (llmStatus.model) setModel(llmStatus.model);
    }
  }, [open, provider, llmStatus]);

  // Fallback: conta não resolveu o modelo → 1º modelo disponível do provider.
  useEffect(() => {
    if (!open || !provider || model) return;
    const list = (modelsData?.models ?? []).filter(
      (m) => m.status === 'active' || m.status === 'available_via_oauth',
    );
    if (list.length > 0) setModel(list[0].model_key);
  }, [open, provider, model, modelsData]);

  function reset() {
    setCompanionId('');
    setProvider('');
    setModel('');
  }

  async function handleConfirm() {
    if (!companionId) return;
    setSubmitting(true);
    try {
      await onConfirm({ companionId, providerKey: provider, modelKey: model });
      reset();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nova conversa</DialogTitle>
          <DialogDescription>
            Escolha o companion e, opcionalmente, o provedor e o modelo da conversa.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <Select
            label="Companion"
            placeholder={companionsLoading ? 'Carregando companions…' : 'Escolha o companion…'}
            options={activeCompanions.map((c) => ({
              value: c.id,
              label: c.name,
              description: c.slug,
            }))}
            value={companionId}
            onChange={setCompanionId}
            disabled={companionsLoading}
          />

          <ProviderSelect
            label="Provedor"
            value={provider}
            onChange={(v) => {
              setProvider(v);
              setModel('');
            }}
          />

          <ModelSelect
            label="Modelo"
            provider={provider || undefined}
            value={model}
            onChange={(v) => setModel(v)}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="brand"
            onClick={() => void handleConfirm()}
            disabled={!companionId}
            loading={submitting}
          >
            Iniciar conversa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
